'use client';

import { type CSSProperties, type ReactNode, useState } from 'react';
import { adaptProductCardToCart } from '../../../cart/cart-utils';
import { useAddToCart, useProductInventory } from '../../../cart/hooks';
import { env } from '../../../lib';
import { useMaxQuantity } from '../../../lib/hooks/use-max-quantity';
import { cn } from '../../../lib/utils';
import { OkendoStarRating } from '../../../reviews/okendo-star-rating';
import { SanityImage } from '../../../sanity/utils/components';
import type { SanityImageProps } from '../../../sanity/utils/fragments';
import { CustomLink } from '../../custom-link';
import type { ProductPageProps } from './client/product-details-types';
import {
  type GiftCardVariant,
  ProductDetailsGiftCardSelector,
} from './product-details-gift-card-selector';

function ProductDetailsFormRoot({
  children,
  className,
  style,
}: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <section
      className={cn(
        'scrollbar-none -mx-2 mb-auto h-min px-2 transition-all ease-linear lg:sticky lg:top-20 lg:max-h-[calc(100vh-5rem)] lg:overflow-y-auto lg:pb-24',
        className
      )}
    >
      <div
        className="grid grid-cols-12 items-baseline gap-[--product-grid-gap] [&>:empty]:hidden"
        style={{ '--product-grid-gap': '16px', ...style } as CSSProperties}
      >
        {children}
      </div>
    </section>
  );
}

function ProductDetailsFormBreadcrumbs({ children }: { children: ReactNode }) {
  return <div className="col-span-full">{children}</div>;
}

function ProductDetailsFormTitle({
  children,
  className,
}: { children: ReactNode; className?: string }) {
  return (
    <h1
      className={cn(
        'col-span-full font-medium text-[hsl(var(--color))]',
        className
      )}
    >
      {children}
    </h1>
  );
}

function ProductDetailsFormRating({
  productGid,
  className,
}: {
  productGid: string;
  className?: string;
}) {
  if (env.NEXT_PUBLIC_SITE_NAME === 'trade') return null;

  return (
    <div className={cn('col-span-full')}>
      <OkendoStarRating productGid={productGid} className={className} />
    </div>
  );
}

function ProductDetailsFormPrice({
  price,
  squareMetersPerBox,
  compareAtPrice,
  prefix,
  suffix,
  textSize,
  className,
}: {
  price: number;
  squareMetersPerBox: number | null;
  compareAtPrice: number | null;
  prefix?: string;
  suffix?: string;
  textSize: 'md' | 'lg';
  className?: string;
}) {
  if (price === null || price === undefined || price <= 0) return null;

  const correctCompareAtPrice = compareAtPrice !== 0 ? compareAtPrice : null;
  const hasDiscount =
    correctCompareAtPrice &&
    correctCompareAtPrice > 0 &&
    correctCompareAtPrice > price;

  return (
    <div className={cn('col-span-full', className)}>
      <div className="grid auto-cols-min grid-flow-col-dense items-baseline gap-2 whitespace-nowrap font-bold text-[hsl(var(--color))]">
        <div className={cn('text-lg', textSize === 'md' && 'text-base')}>
          {prefix}
          {squareMetersPerBox ? (
            <>
              {hasDiscount && (
                <span className="mr-2 inline-block text-gray-500 line-through">
                  ${compareAtPrice?.toFixed(0) ?? ''}
                </span>
              )}
              <span className="inline-block">${price.toFixed(0)}</span>/box ={' '}
              <span className="inline-block">
                ${Number((price / squareMetersPerBox).toFixed(0))}
              </span>
              /m<sup>2</sup>
            </>
          ) : (
            <>
              {hasDiscount && (
                <span className="mr-2 inline-block text-gray-500 line-through">
                  ${compareAtPrice?.toFixed(0) ?? ''}
                </span>
              )}
              <span className="inline-block">${price.toFixed(0)}</span>
            </>
          )}
          {suffix}
        </div>
      </div>
    </div>
  );
}

function ProductDetailsFormVariants({ children }: { children: ReactNode }) {
  return <div className="col-span-12">{children}</div>;
}

function ProductDetailsFormQuantity({ children }: { children: ReactNode }) {
  return <div className="col-span-12">{children}</div>;
}

function ProductDetailsFormGiftCardSelector({
  product,
  variants,
  domain,
  buttonClassNames,
  className,
}: {
  product: ProductPageProps;
  variants: GiftCardVariant[];
  domain: string;
  buttonClassNames?: string;
  className?: string;
}) {
  const [selectedVariant, setSelectedVariant] =
    useState<GiftCardVariant | null>(null);
  const { addItem } = useAddToCart({ domain });
  const { data: inventory, isLoading: isInventoryLoading } =
    useProductInventory(product.productGid);

  const isOutOfStock = !inventory?.availableForSale && !isInventoryLoading;

  function handleAddToCart() {
    if (!selectedVariant?.gid || !product.productGid) return;

    const cartData = adaptProductCardToCart({
      title: product.title,
      slug: product.slug,
      productGid: product.productGid,
      variantGid: selectedVariant.gid,
      price: selectedVariant.price ?? 0,
      category: product.category,
      mainImage: product.mainImage,
      allowBackorder: product.allowBackorder ?? false,
      squareMetersPerBox: product.squareMetersPerBox,
    });

    addItem({
      product: cartData.product,
      variant: cartData.variant,
      quantity: 1,
      maxQuantityAvailable: 99999,
      productUrl:
        product.category === 'SAMPLE' ? `/products/${product.slug}` : undefined,
    });
  }

  return (
    <div className="col-span-12 grid gap-4">
      <ProductDetailsGiftCardSelector
        variants={variants}
        selectedVariant={selectedVariant}
        onVariantChange={setSelectedVariant}
        className={className}
      />
      <button
        type="button"
        onClick={handleAddToCart}
        disabled={isOutOfStock || !selectedVariant?.gid}
        className={cn(
          'flex w-full items-center justify-center gap-4',
          buttonClassNames
        )}
      >
        <span>{isOutOfStock ? 'Sold Out' : 'Add to Cart'}</span>
        <div className="grid auto-cols-min grid-flow-col-dense items-baseline gap-2 whitespace-nowrap">
          {selectedVariant?.price ? (
            <span>${selectedVariant?.price}</span>
          ) : null}
        </div>
      </button>
    </div>
  );
}

function ProductDetailsFormButtons({ children }: { children: ReactNode }) {
  return <div className="col-span-12">{children}</div>;
}

type ComplimentaryProduct = NonNullable<
  ProductPageProps['complimentaryProducts']
>[number];

function ComplimentaryProducts({
  products,
  buttonClassNames,
  titleClassName,
  domain,
}: {
  products: ComplimentaryProduct[];
  buttonClassNames?: string;
  titleClassName?: string;
  domain: string;
}) {
  if (!products?.length) return null;

  const hasOnlyOneProduct = products.length === 1;

  return (
    <main className="col-span-full text-[hsl(var(--color))]">
      <h2 className={cn('mb-2 font-medium', titleClassName)}>
        Pairs well with ...
      </h2>
      {hasOnlyOneProduct && products[0] && (
        <SingleComplimentaryProduct
          product={products[0]}
          buttonClassNames={buttonClassNames}
          domain={domain}
        />
      )}
      {!hasOnlyOneProduct && (
        <MultipleComplimentaryProducts
          products={products}
          buttonClassNames={buttonClassNames}
        />
      )}
    </main>
  );
}

function SingleComplimentaryProduct({
  product,
  buttonClassNames,
  domain,
}: {
  product: ComplimentaryProduct;
  buttonClassNames?: string;
  domain: string;
}) {
  const { addItem } = useAddToCart({ domain });
  const { data: inventory, isLoading: isInventoryLoading } =
    useProductInventory(product.productGid);
  const maxQuantityAvailable = useMaxQuantity({
    productGid: product.productGid,
    variantGid: product.variantGid,
    allowBackorder: product.allowBackorder ?? false,
  });

  const isOutOfStock = !inventory?.availableForSale && !isInventoryLoading;

  function handleAddToCart() {
    if (!product.variantGid || !product.productGid) return;

    const cartData = adaptProductCardToCart({
      title: product.title,
      slug: product.slug,
      productGid: product.productGid,
      variantGid: product.variantGid,
      price: product.price ?? 0,
      category: product.category,
      mainImage: product.mainImage,
      allowBackorder: product.allowBackorder ?? false,
      squareMetersPerBox: product.squareMetersPerBox,
    });

    addItem({
      product: cartData.product,
      variant: cartData.variant,
      quantity: 1,
      maxQuantityAvailable,
      productUrl:
        product.category === 'SAMPLE' ? `/products/${product.slug}` : undefined,
    });
  }

  return (
    <article className="relative grid gap-2 rounded-theme-sm border border-gray-400 p-4">
      <div className="flex items-center">
        <div className="relative mr-2 h-16 w-16 overflow-hidden">
          {product.mainImage && (
            <SanityImage
              image={product.mainImage}
              width={128}
              height={128}
              priority
              className="h-full w-full object-cover"
              style={{ objectPosition: '50% 50%' }}
            />
          )}
        </div>
        <h3 className="product-card-title--small inline-flex">
          <CustomLink
            href={`/products/${product.slug}`}
            className="hf:underline hf:outline-none outline-none"
          >
            {product.title}
          </CustomLink>
        </h3>
      </div>
      <button
        type="button"
        onClick={handleAddToCart}
        disabled={isOutOfStock}
        className={cn(
          'flex w-full items-center justify-center gap-4',
          buttonClassNames
        )}
      >
        <span>{isOutOfStock ? 'Sold Out' : 'Add to Cart'}</span>
        <div className="grid auto-cols-min grid-flow-col-dense items-baseline gap-2 whitespace-nowrap">
          <span>${product.price}</span>
        </div>
      </button>
    </article>
  );
}

function MultipleComplimentaryProducts({
  products,
  buttonClassNames,
}: {
  products: ComplimentaryProduct[];
  buttonClassNames?: string;
}) {
  return (
    <div
      className={cn(
        'relative grid snap-x snap-mandatory grid-flow-col-dense gap-4 overflow-x-auto pb-4',
        '[&::-webkit-scrollbar]:h-[3px]',
        '[&::-webkit-scrollbar-track]:bg-gray-100',
        '[&::-webkit-scrollbar-track]:rounded-full',
        '[&::-webkit-scrollbar-thumb]:bg-[hsl(var(--color))]',
        '[&::-webkit-scrollbar-thumb]:rounded-full'
      )}
      style={{ gridAutoColumns: '170px' }}
    >
      {products.map((product) => (
        <MultipleComplimentaryProductCard
          key={product._createdAt}
          product={product}
          buttonClassNames={buttonClassNames}
        />
      ))}
    </div>
  );
}

function MultipleComplimentaryProductCard({
  product,
  buttonClassNames,
}: {
  product: ComplimentaryProduct;
  buttonClassNames?: string;
}) {
  const { data: inventory, isLoading: isInventoryLoading } =
    useProductInventory(product.productGid);

  const isOutOfStock = !inventory?.availableForSale && !isInventoryLoading;

  return (
    <CustomLink href={`/products/${product.slug}`}>
      <article className="relative flex h-full flex-col items-stretch space-y-1 hf:outline-none outline-none">
        <div className="relative grid h-0 w-full pb-[100%]">
          <div
            className="absolute inset-0 h-full w-full overflow-hidden"
            style={{ background: 'transparent' }}
          >
            {product.mainImage && (
              <SanityImage
                image={product.mainImage}
                width={170}
                height={334}
                priority
                className="inset-0 h-full w-full object-cover"
                style={{ objectPosition: '50% 50%' }}
              />
            )}
          </div>
        </div>
        <h3 className="inline-flex pt-2 font-semibold text-sm leading-[1.2] tracking-tight hf:underline">
          {product.title}
        </h3>
        <span className="product-price grid auto-cols-min grid-flow-col-dense items-baseline gap-2 whitespace-nowrap font-medium">
          ${product.price}
        </span>
        <div className="mt-auto flex w-full flex-1 flex-col justify-end self-end text-center">
          <span className={cn('uppercase', buttonClassNames)}>
            {isOutOfStock ? 'Sold Out' : 'View'}
          </span>
        </div>
      </article>
    </CustomLink>
  );
}

function ProductDetailsFormAccordion({ children }: { children: ReactNode }) {
  return <div className="col-span-12">{children}</div>;
}

function ProductDetailsFormTable({ children }: { children: ReactNode }) {
  return <div>{children}</div>;
}

function ProductDetailsFormTitleSection({ children }: { children: ReactNode }) {
  return <div className="col-span-full grid gap-1">{children}</div>;
}

function ProductDetailsFormPreOrderInfo() {
  return (
    <div className="col-span-full mt-4 text-[hsl(var(--color))] text-sm">
      <div className="relative inline-flex">
        <div className="size-2 rounded-full bg-yellow-400" />
        <div className="absolute inset-0 size-2 animate-ping rounded-full bg-yellow-400 [animation-duration:2s]" />
      </div>
      <span className="ml-2 font-semibold">Pre-order:</span> If you order a
      pre-order item the entire order will ship once the pre-order item is in
      stock. To receive in-stock items sooner, please place separate orders.
    </div>
  );
}

function ProductDetailsFormPromotionalImage({
  image,
}: { image: SanityImageProps }) {
  return (
    <div className="col-span-full">
      <SanityImage
        image={image}
        width={image.asset?.metadata?.dimensions?.width ?? undefined}
        height={image.asset?.metadata?.dimensions?.height ?? undefined}
        className="w-full"
      />
    </div>
  );
}

ProductDetailsFormRoot.Breadcrumbs = ProductDetailsFormBreadcrumbs;
ProductDetailsFormRoot.Title = ProductDetailsFormTitle;
ProductDetailsFormRoot.TitleSection = ProductDetailsFormTitleSection;
ProductDetailsFormRoot.Rating = ProductDetailsFormRating;
ProductDetailsFormRoot.Price = ProductDetailsFormPrice;
ProductDetailsFormRoot.Variants = ProductDetailsFormVariants;
ProductDetailsFormRoot.GiftCardSelector = ProductDetailsFormGiftCardSelector;
ProductDetailsFormRoot.Quantity = ProductDetailsFormQuantity;
ProductDetailsFormRoot.Buttons = ProductDetailsFormButtons;
ProductDetailsFormRoot.ComplimentaryProducts = ComplimentaryProducts;
ProductDetailsFormRoot.Accordion = ProductDetailsFormAccordion;
ProductDetailsFormRoot.Table = ProductDetailsFormTable;
ProductDetailsFormRoot.PreOrderInfo = ProductDetailsFormPreOrderInfo;
ProductDetailsFormRoot.PromotionalImage = ProductDetailsFormPromotionalImage;

export const ProductDetailsForm = ProductDetailsFormRoot;
